import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import adminApi from '../services/adminApi';

function UsersPage() {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();

  const loadUsers = useCallback(async (page = 1) => {
    try {
      setIsLoading(true);
      const response = await adminApi.searchUsers({
        query: searchQuery,
        page,
        limit: 20,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });
      setUsers(response.users || []);
      setPagination(response.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    loadUsers(1);
  };

  const handlePageChange = (newPage) => {
    loadUsers(newPage);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  return (
    <div>
      <div className="admin-page-header">
        <h1>Users</h1>
        <p>Search and manage user accounts</p>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="admin-search-box">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by email, name, or phone..."
        />
        <button type="submit" className="admin-btn admin-btn-primary">
          Search
        </button>
      </form>

      {error && (
        <div className="admin-card" style={{ marginBottom: '1rem' }}>
          <p style={{ color: '#dc2626', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Users Table */}
      <div className="admin-card">
        {isLoading ? (
          <div className="admin-loading" style={{ padding: '2rem' }}>
            <div className="admin-spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="admin-empty-state">
            <p>No users found</p>
          </div>
        ) : (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Registered</th>
                  <th>Meal Plans</th>
                  <th>Generated</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user._id}>
                    <td>
                      <strong>{user.email}</strong>
                    </td>
                    <td>{user.name || '-'}</td>
                    <td style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      {formatDate(user.createdAt)}
                    </td>
                    <td>
                      <span className="admin-badge admin-badge-info">
                        {user.mealPlanCount || 0} plans
                      </span>
                    </td>
                    <td>
                      <span className="admin-badge admin-badge-info">
                        {user.totalPlansGenerated || 0} total
                      </span>
                    </td>
                    <td>
                      {user.googleId ? (
                        <span className="admin-badge admin-badge-success">Google</span>
                      ) : user.appleId ? (
                        <span className="admin-badge admin-badge-info">Apple</span>
                      ) : (
                        <span className="admin-badge admin-badge-warning">Email</span>
                      )}
                    </td>
                    <td>
                      {user.isPremium ? (
                        <div>
                          <span className="admin-badge admin-badge-success">Premium</span>
                          {user.stripePlanName && (
                            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                              {user.stripePlanName}
                            </div>
                          )}
                          {user.premiumExpiresAt && (
                            <div style={{ fontSize: '0.75rem', color: new Date(user.premiumExpiresAt) < new Date() ? '#dc2626' : '#6b7280', marginTop: '0.25rem' }}>
                              {new Date(user.premiumExpiresAt) < new Date() ? 'Expired' : 'Expires'}: {formatDate(user.premiumExpiresAt)}
                            </div>
                          )}
                        </div>
                      ) : user.stripeCustomerId ? (
                        <div>
                          <span className="admin-badge admin-badge-danger">Cancelled</span>
                        </div>
                      ) : (
                        <span className="admin-badge admin-badge-info">Free</span>
                      )}
                      {user.isAdmin && (
                        <span className="admin-badge admin-badge-warning" style={{ marginLeft: '0.25rem' }}>Admin</span>
                      )}
                    </td>
                    <td>
                      <button 
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        onClick={() => navigate(`/admin/users/${user._id}`)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="admin-pagination">
                <button
                  disabled={pagination.page <= 1}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  ← Previous
                </button>
                
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const page = pagination.page <= 3 
                    ? i + 1 
                    : pagination.page + i - 2;
                  
                  if (page < 1 || page > pagination.totalPages) return null;
                  
                  return (
                    <button
                      key={page}
                      className={pagination.page === page ? 'active' : ''}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Next →
                </button>
              </div>
            )}

            <p style={{ textAlign: 'center', color: '#6b7280', marginTop: '1rem', fontSize: '0.875rem' }}>
              Showing {users.length} of {pagination.total} users
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default UsersPage;
